/* eslint-disable max-lines */
import { waitForWindowVariable } from "common/utils/ui/wait_for";
import { redirectTo } from "common/utils/browser";
import { api } from "jiffy-api";
import {
  logUploadErrorEvent,
  getUploadErrorMessage
} from "common/utils/mixpanel/dropzone-error-event-tracker";
import { trackRudderstackImageUploadStarted } from "common/utils/rudderstack/tracker";
import initUploadFiles from "components/jiffy/upload-files/upload-files";
import { submitDtfEvent, submitCompletedEvent } from "common/utils/mixpanel/submit-dtf-events";
import ProductDetailsDtfProgress from "components/jiffy/product-details-dtf-sheet/product-details-dtf-progress/product-details-dtf-progress";
import { isFileTypeSupported, logUnsupportedFileTypeEvent } from "common/file-type-helper";
import "components/jiffy/product-details-made/product-details-made";
import "./product-details-transfer.scss";

const $getRootElements = () => document.querySelectorAll(".js-product-details-transfer");
const $getErrorElement = $root => $root.querySelector(".js-dtf-upload-file-error");
const $getVisibleUploadArea = $root => $root.querySelector(".js-upload-files-row-default");
const $getUploadError = $root => $root.querySelector(".js-transfer-upload-error");
const $getTransferContent = $root => $root.querySelector(".js-transfer-content");
const $getTransferProgress = $root => $root.querySelector(".js-transfer-progress");
const $getProgressText = () => document.querySelector(".js-pdp-dtf-progress-text");
const $getTransferData = () => document.querySelector(".js-transfer-data");
const $getPreviewWrapper = () => document.querySelector(".upload-files__preview-wrapper");
const $getImage = () => document.querySelector(".js-dz-thumbnail");
const $getCtaDropAreaElement = () => document.querySelector(".js-dtf-pdp-cta-drop-area");
const $getCtaPricingElement = () => document.querySelector(".js-dtf-pdp-cta-pricing");
const $getCtaActualPriceElement = () => document.querySelector(".js-dtf-pdp-cta-actual-price");
const $getUploadFileHelpUrl = () => document.querySelector(".js-upload-files-help-block-link");
const $getPdfSpinner = () => document.querySelector(".js-pdf-spinner");

const updateProgressText = message => {
  $getProgressText().textContent = message;
};

const onProgressStart = $rootElements => {
  $rootElements.forEach($root => {
    $getUploadError($root).classList.add("hidden");
    $getTransferContent($root).classList.add("hidden");
    $getTransferProgress($root).classList.remove("hidden");
    $getProgressText().classList.remove("hidden");
    updateProgressText("");
  });
};

const reset = $rootElements => {
  if (!$rootElements) return;
  $rootElements.forEach($root => {
    $getUploadError($root).classList.add("hidden");
    $getTransferContent($root).classList.remove("hidden");
    $getTransferProgress($root).classList.add("hidden");
    $getProgressText().classList.add("hidden");
    updateProgressText("");
  });
};

const setProgressBars = (progressBars, file, progress) => {
  progressBars.forEach(progressBar => {
    progressBar.start();
    progressBar.setFilename(file.name);
    progressBar.setValues(progress);
  });
  if (progress === 100) {
    updateProgressText("Uploading file");
  }
};

let uploadStartTime;

const setDTFUserSpecificSalePrice = () => {
  api.dtf
    .pdpCtaSalePrice()
    .then(data => {
      const { is_dtf_on_sale: isDtfOnSale, sale_price: salePrice } = data;

      if (isDtfOnSale) {
        const $ctaPriceElement = $getCtaActualPriceElement();
        const $ctaPricingElement = $getCtaPricingElement();
        const $ctaDropAreaElement = $getCtaDropAreaElement();
        const pricingSaleClass = "product-details-transfer__pricing--sale";
        const dropAreaSaleClass = "product-details-transfer__drop-area--sale";

        if (!$ctaPriceElement.textContent.includes(salePrice))
          $ctaPriceElement.textContent = salePrice;
        if (!$ctaPricingElement.classList.contains(pricingSaleClass))
          $ctaPricingElement.classList.add(pricingSaleClass);
        if (!$ctaDropAreaElement.classList.contains(dropAreaSaleClass))
          $ctaDropAreaElement.classList.add(dropAreaSaleClass);
      }
    })
    .catch(_error => {
      // Do nothing
    });
};

const hideProgressBars = progressBars => {
  progressBars.forEach(progressBar => {
    progressBar.hide();
  });
};

const handleErrors = (dropzone, $rootElements) => {
  $rootElements.forEach($rootElement => {
    dropzone.on("error", (file, response) => {
      // success can be true or false, but it came from server response which
      // means upload happened.
      if (typeof response === "object" && typeof response.success === "boolean") {
        logUploadErrorEvent({ pageValue: "cart" }, file, response);
        submitCompletedEvent({
          pdpType: "blanks",
          uploadedOn: "pdp",
          file,
          response,
          uploadStartTime
        });
      }
      const errorText = getUploadErrorMessage(response);
      const $errorElement = $getErrorElement($rootElement);
      const $visibleUploadArea = $getVisibleUploadArea($rootElement);

      $getUploadError($rootElement).classList.remove("hidden");
      $getTransferProgress($rootElement).classList.add("hidden");

      if (!$errorElement || !$visibleUploadArea) return;

      const $dzPreviewElement =
        typeof dropzone.options.previewsContainer === "string"
          ? document.querySelector(dropzone.options.previewsContainer)
          : dropzone.options.previewsContainer;

      const $pdfSpinner = $getPdfSpinner();
      if (file.type === "application/pdf") {
        $dzPreviewElement.classList.remove("hidden");
        $pdfSpinner.classList.add("hidden");
      }

      $errorElement.textContent = errorText;
      $visibleUploadArea.classList.add("upload-files__file-row-control--error");
    });
  });
};

const getPageValue = () =>
  $getTransferData() ? $getTransferData().getAttribute("data-page") : "pdp";

const getPdpType = () => ($getTransferData() ? "" : "blanks");

const ANALYTICS_ORIGIN_LOCATION = "Page content";

const trackAddFileAreaClick = () => {
  submitDtfEvent("transfer_choose_file_clicked", {
    page: getPageValue(),
    pdp_type: getPdpType(),
    location: ANALYTICS_ORIGIN_LOCATION
  });
};

const trackFileCount = (length = 1) => {
  submitDtfEvent("image_upload_count", {
    page: getPageValue(),
    image_count: length
  });
};

const trackImageUploadStart = (file, isDropEvent) => {
  submitDtfEvent("image_upload_started", {
    page: getPageValue(),
    pdp_type: getPdpType(),
    type: isDropEvent ? "drop" : "browse",
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    dpi: 300,
    project_id: null,
    location: ANALYTICS_ORIGIN_LOCATION
  });
  trackRudderstackImageUploadStarted({
    dpi: 300,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    location: getPageValue(),
    business_line: "Transfers",
    artworks_count: 1
  });
};

window.addEventListener(
  "pageshow",
  e => {
    // if page was cached (Safari case)
    if (e.persisted) {
      if (!window.Dropzone) return;
      const dropzone = Dropzone.forElement(document.body);
      dropzone.removeAllFiles(true);
      reset([...$getRootElements()]);
    }
  },
  false
);

// eslint-disable-next-line max-lines-per-function
const initPDPTransfer = () => {
  const $rootElements = [...$getRootElements()];
  const progressBars = $rootElements.map($root => new ProductDetailsDtfProgress($root));
  let isDropEvent = false;

  // eslint-disable-next-line max-lines-per-function
  waitForWindowVariable("Dropzone", () => {
    const dropzone = Dropzone.forElement(document.body);
    const $clickableArea = dropzone.clickableElements;

    // .js-upload-files-previews
    const $dzPreviewElement =
      typeof dropzone.options.previewsContainer === "string"
        ? document.querySelector(dropzone.options.previewsContainer)
        : dropzone.options.previewsContainer;

    $clickableArea.forEach($area => $area?.addEventListener("click", trackAddFileAreaClick));

    dropzone.on("sending", (file, xhr, formData) => {
      formData.append("generate_link", true);
      formData.append("product_id", $getRootElements()[0]?.getAttribute("data-product-id"));
    });

    dropzone.on("drop", () => {
      isDropEvent = true;
    });

    dropzone.on("addedfile", file => {
      const { valid, message } = isFileTypeSupported(file, false);
      if (!valid) {
        dropzone.emit("error", file, { skipEvents: true, error: message });
        const $rootElement = $getRootElements()[0];
        $getTransferContent($rootElement).classList.add("hidden");
        logUnsupportedFileTypeEvent(file, {
          page: getPageValue(),
          pdp_type: getPdpType()
        });
        return;
      }
      uploadStartTime = performance.now();
      trackFileCount();
      trackImageUploadStart(file, isDropEvent);
      onProgressStart($rootElements);
      $getPreviewWrapper().classList.add("upload-files__preview-wrapper--force-width-small");
      $getImage().classList.add("upload-files__image--force-width-small");

      const $pdfSpinner = $getPdfSpinner();
      if (file.type === "application/pdf") {
        $dzPreviewElement.classList.add("hidden");
        $pdfSpinner.classList.remove("hidden");
      }
    });

    dropzone.on("uploadprogress", (file, progress) => {
      setProgressBars(progressBars, file, progress);
    });

    dropzone.on("complete", () => {
      $getProgressText().classList.add("hidden");
    });

    dropzone.on("success", (file, response) => {
      hideProgressBars(progressBars);
      submitCompletedEvent({
        pdpType: "blanks",
        uploadedOn: "pdp",
        location: ANALYTICS_ORIGIN_LOCATION,
        file,
        response,
        uploadStartTime
      });
      response.redirect_url && redirectTo(response.redirect_url);
    });

    handleErrors(dropzone, $rootElements);
  });

  setDTFUserSpecificSalePrice();

  const $helpUrl = $getUploadFileHelpUrl();
  $helpUrl?.addEventListener("click", e => {
    e.stopPropagation();
  });

  initUploadFiles();
};

export default initPDPTransfer;
